import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Star, X, Clock, Send, Camera, Image as ImageIcon } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { createPortal } from "react-dom";

const TEAL = "#0D9488";

/** Client-side dialog to leave a 1-5 star rating on a coach.
 *  Only fetches eligibility (30-day rule + already-rated) on open.
 *  Props:
 *    - open, onClose(saved:boolean)
 *    - coachId (required)
 *    - coachName / coachEmail (for label)
 */
export default function RateCoachDialog({ open, onClose, coachId, coachName, coachEmail }) {
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !coachId) return;
    setLoading(true);
    setStars(0);
    setComment("");
    setPhoto(null);
    api.get(`/coaches/${coachId}/ratings`)
      .then(({ data }) => setInfo(data))
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, [open, coachId]);

  if (!open) return null;

  const submit = async () => {
    if (!stars) return;
    setBusy(true);
    try {
      await api.post(`/coaches/${coachId}/ratings`, { stars, comment: comment.trim() || null, photo_data_url: photo || null });
      toast.success("Thanks for the review!");
      onClose?.(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const label = coachName || coachEmail || "your coach";

  return createPortal(
    <div
      className="fixed inset-0 z-[9990] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => onClose?.(false)}
      data-testid="rate-coach-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="rate-coach-dialog"
      >
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#0D9488]">Rate your coach</div>
            <h3 className="font-display font-bold text-lg text-slate-900 mt-0.5">
              How's it going with {label}?
            </h3>
          </div>
          <button
            type="button"
            onClick={() => onClose?.(false)}
            className="text-slate-400 hover:text-slate-700"
            data-testid="rate-coach-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="h-24 bg-slate-50 rounded animate-pulse" />
          ) : info?.already_rated ? (
            <div className="text-sm text-slate-600 py-4">
              You already reviewed this coach. Thanks!
            </div>
          ) : !info?.can_rate ? (
            <div className="flex items-start gap-3 py-2">
              <Clock className="w-5 h-5 mt-0.5 text-amber-600 shrink-0" />
              <div>
                <div className="text-sm font-semibold text-slate-900">Ratings unlock after 30 days</div>
                <div className="text-[12px] text-slate-500 mt-1 leading-relaxed">
                  {info?.days_shared !== null && info?.days_shared !== undefined ? (
                    <>You've been sharing with this coach for <b>{info.days_shared} day{info.days_shared === 1 ? "" : "s"}</b>. Come back in <b>{info.days_until_eligible} day{info.days_until_eligible === 1 ? "" : "s"}</b> to leave a review.</>
                  ) : (
                    <>You need an active share with this coach before you can rate them.</>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Your rating</div>
                <div className="flex items-center gap-1" data-testid="rate-coach-stars">
                  {[1, 2, 3, 4, 5].map((n) => {
                    const active = n <= (hover || stars);
                    return (
                      <button
                        key={n}
                        type="button"
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        onClick={() => setStars(n)}
                        className={`w-10 h-10 rounded flex items-center justify-center transition-transform ${active ? "scale-110" : "opacity-60 hover:opacity-100"}`}
                        data-testid={`rate-coach-star-${n}`}
                        aria-label={`${n} star${n === 1 ? "" : "s"}`}
                      >
                        <Star className={`w-7 h-7 ${active ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Comment (optional)</div>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value.slice(0, 400))}
                  placeholder="What did they help you with?"
                  rows={3}
                  className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] text-sm"
                  data-testid="rate-coach-comment"
                />
                <div className="text-[10px] text-slate-400 font-mono-num mt-1">{comment.length}/400</div>
              </div>

              {/* Photo attachment — 5-star + photo lands in hall-of-fame */}
              <div>
                <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 flex items-center gap-1">
                  <Camera className="w-3 h-3" /> Attach a photo (optional)
                  {stars === 5 && photo && (
                    <span className="ml-1 text-amber-600 font-semibold">★ Auto-pins to hall of fame</span>
                  )}
                </div>
                {photo ? (
                  <div className="relative rounded-lg overflow-hidden border border-slate-200" data-testid="rate-coach-photo-preview">
                    <img src={photo} alt="review" className="w-full h-32 object-cover" />
                    <button
                      type="button"
                      onClick={() => setPhoto(null)}
                      className="absolute top-2 right-2 w-6 h-6 bg-white/90 rounded-md flex items-center justify-center text-rose-600 shadow"
                      data-testid="rate-coach-photo-remove"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="rate-coach-photo-input"
                    className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-lg py-4 cursor-pointer hover:border-teal-300 hover:bg-teal-50/40 text-slate-500"
                    data-testid="rate-coach-photo-dropzone"
                  >
                    <ImageIcon className="w-4 h-4" />
                    <span className="text-[11px] uppercase tracking-widest font-semibold">Upload PNG/JPG · max 2MB</span>
                  </label>
                )}
                <input
                  id="rate-coach-photo-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="rate-coach-photo-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (!f.type.startsWith("image/")) { toast.error("Please choose an image."); return; }
                    if (f.size > 2_000_000) { toast.error("Image too big (2MB max)."); return; }
                    const r = new FileReader();
                    r.onload = () => setPhoto(r.result);
                    r.readAsDataURL(f);
                  }}
                />
              </div>
              {info?.avg_rating !== null && info?.rating_count > 0 && (
                <div className="text-[11px] text-slate-500 flex items-center gap-1">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> Current avg: <b>{info.avg_rating}</b> · <span className="tabular-nums">{info.rating_count}</span> review{info.rating_count === 1 ? "" : "s"}
                </div>
              )}
            </div>
          )}

          {/* Recent reviews strip — always visible when there are prior reviews */}
          {!loading && info?.recent && info.recent.length > 0 && (
            <div className="mt-5 border-t border-slate-100 pt-4">
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Recent reviews</div>
              <ul className="space-y-2 max-h-52 overflow-auto" data-testid="rate-coach-recent">
                {info.recent.slice(0, 6).map((r) => (
                  <li key={r.id} className="border border-slate-100 rounded-md p-2.5 bg-slate-50/40" data-testid={`rate-coach-recent-${r.id}`}>
                    <div className="flex items-center gap-1 text-[11px]">
                      <div className="flex items-center gap-0.5">
                        {[1,2,3,4,5].map((n) => (
                          <Star key={n} className={`w-3 h-3 ${n <= r.stars ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                        ))}
                      </div>
                      <span className="text-slate-400 font-mono-num ml-1">@{r.reviewer_handle}</span>
                    </div>
                    {r.comment && (
                      <div className="text-[12px] text-slate-700 mt-1 leading-relaxed">{r.comment}</div>
                    )}
                    {r.response && (
                      <div className="mt-2 pl-3 border-l-2 border-teal-300 bg-teal-50/40 rounded-r py-1.5 pr-2">
                        <div className="text-[9px] uppercase tracking-widest font-semibold text-teal-700 mb-0.5">Coach reply</div>
                        <div className="text-[12px] text-slate-700 leading-relaxed">{r.response}</div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50/60">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onClose?.(false)}
            className="text-slate-700 hover:bg-slate-100"
            data-testid="rate-coach-cancel"
          >
            Close
          </Button>
          {info?.can_rate && !info?.already_rated && (
            <Button
              type="button"
              size="sm"
              disabled={!stars || busy}
              onClick={submit}
              className="text-white"
              style={{ background: TEAL }}
              data-testid="rate-coach-submit"
            >
              <Send className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Sending…" : "Submit review"}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
